import { useRef, useState } from "react";
import { FileText, Trash2, Upload, ExternalLink, Pencil, Check, X } from "lucide-react";
import { toast } from "sonner";
import { useDemo } from "@/store/DemoStore";
import { Card, CardTitle, btn } from "@/components/common";
import { fmtDate } from "@/lib/trip";
import { cn } from "@/lib/utils";

const fmtSize = (b = 0) => (b >= 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);
const docUrl = (tripId, docId) => `${process.env.REACT_APP_BACKEND_URL}/api/trips/${tripId}/documents/${docId}`;
const stripExt = (n = "") => n.replace(/\.pdf$/i, "");

function DocRow({ trip, doc, canDelete, canRename }) {
  const { renameTripDocument, deleteTripDocument } = useDemo();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(stripExt(doc.name));
  const [removing, setRemoving] = useState(false);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    const name = draft.trim();
    if (!name) return toast.error("Please enter a document name");
    setSaving(true);
    const r = await renameTripDocument(trip.id, doc.id, name);
    setSaving(false);
    if (r) setEditing(false);
  };
  const remove = async () => { setRemoving(true); await deleteTripDocument(trip.id, doc.id); setRemoving(false); };

  return (
    <li className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-3" data-testid={`document-row-${doc.id}`}>
      <span className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-rose-50 text-rose-600"><FileText className="h-5 w-5" /></span>
      <div className="min-w-0 flex-1">
        {editing ? (
          <div className="flex items-center gap-1.5">
            <input autoFocus value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") setEditing(false); }}
              data-testid={`document-rename-input-${doc.id}`} className="min-w-0 flex-1 rounded-lg border border-slate-300 px-2.5 py-1.5 text-sm outline-none focus:border-navy-900" />
            <span className="shrink-0 text-xs text-slate-400">.pdf</span>
            <button onClick={save} disabled={saving} data-testid={`document-rename-save-${doc.id}`} className="grid h-8 w-8 place-items-center rounded-lg text-emerald-600 hover:bg-emerald-50 disabled:opacity-50" aria-label="Save name"><Check className="h-4 w-4" /></button>
            <button onClick={() => { setDraft(stripExt(doc.name)); setEditing(false); }} data-testid={`document-rename-cancel-${doc.id}`} className="grid h-8 w-8 place-items-center rounded-lg text-slate-400 hover:bg-slate-100" aria-label="Cancel"><X className="h-4 w-4" /></button>
          </div>
        ) : (
          <>
            <p className="truncate text-sm font-medium text-navy-900" title={doc.name}>{doc.name}</p>
            <p className="text-xs text-slate-500">{fmtSize(doc.size)} · {doc.uploadedBy} · {fmtDate(doc.uploadedOn)}</p>
          </>
        )}
      </div>
      {!editing && (
        <>
          {canRename && (
            <button onClick={() => { setDraft(stripExt(doc.name)); setEditing(true); }} data-testid={`document-rename-${doc.id}`} className="grid h-9 w-9 shrink-0 place-items-center rounded-lg text-slate-400 transition-colors hover:bg-slate-100 hover:text-navy-900" aria-label="Rename document"><Pencil className="h-4 w-4" /></button>
          )}
          <a href={docUrl(trip.id, doc.id)} target="_blank" rel="noopener noreferrer" data-testid={`document-view-${doc.id}`} className={cn(btn.secondary, "h-9 px-3")}><ExternalLink className="h-4 w-4" />View</a>
          {canDelete && (
            <button onClick={remove} disabled={removing} data-testid={`document-delete-${doc.id}`} className="grid h-9 w-9 shrink-0 place-items-center rounded-lg text-slate-400 transition-colors hover:bg-red-50 hover:text-red-600 disabled:opacity-50" aria-label="Delete document"><Trash2 className="h-4 w-4" /></button>
          )}
        </>
      )}
    </li>
  );
}

export function TripDocuments({ trip, canDelete = false }) {
  const { uploadTripDocument, me } = useDemo();
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const docs = [...(trip.documents || [])].sort((a, b) => (b.uploadedOn || "").localeCompare(a.uploadedOn || ""));

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.type !== "application/pdf" && !file.name.toLowerCase().endsWith(".pdf")) return toast.error("Please choose a PDF file");
    if (file.size > 15 * 1024 * 1024) return toast.error("File must be 15MB or smaller");
    setBusy(true);
    await uploadTripDocument(trip.id, file);
    setBusy(false);
  };

  const canRenameDoc = (doc) => canDelete || (me?.kind === "client" && doc.uploaderRole === "client");

  return (
    <Card data-testid="trip-documents">
      <CardTitle>Documents</CardTitle>
      <div className="p-5 sm:p-6">
        <div className="flex flex-col gap-3 rounded-2xl border border-dashed border-slate-300 bg-slate-50/60 p-5 text-center sm:flex-row sm:items-center sm:justify-between sm:text-left">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-navy-900">Share travel documents</p>
            <p className="mt-0.5 text-xs text-slate-500">Upload PDFs like boarding passes, confirmations and itineraries (max 15MB). {canDelete ? "You can rename or remove documents at any time." : "You can rename documents you upload; your advisor can remove documents if needed."}</p>
          </div>
          <input ref={fileRef} type="file" accept="application/pdf,.pdf" hidden onChange={onPick} data-testid="document-file-input" />
          <button onClick={() => fileRef.current?.click()} disabled={busy} data-testid="document-upload-btn" className={cn(btn.primary, "h-10 shrink-0", busy && "opacity-60")}>
            <Upload className="h-4 w-4" />{busy ? "Uploading…" : "Upload PDF"}
          </button>
        </div>

        {docs.length === 0 ? (
          <p className="mt-5 text-center text-sm text-slate-400" data-testid="documents-empty">No documents have been uploaded yet.</p>
        ) : (
          <ul className="mt-5 space-y-2.5" data-testid="documents-list">
            {docs.map((d) => <DocRow key={d.id} trip={trip} doc={d} canDelete={canDelete} canRename={canRenameDoc(d)} />)}
          </ul>
        )}
      </div>
    </Card>
  );
}
